import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { DollarSign, Clock, MessageSquare, CheckCircle } from "lucide-react";

const PricingSection = () => {
  const handleWhatsApp = () => {
    window.open("https://wa.me/6588921053?text=Hi, I'd like to know more about your pricing", "_blank");
  };

  const pricingFeatures = [
    "Professional licensed drivers",
    "Safe and reliable",
    "No hidden charges",
    "Flexible payment options"
  ];

  return (
    <section id="pricing" className="py-24 px-6 bg-gradient-to-b from-yellow-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Transparent Pricing
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Clear, competitive rates with no surprises. Pay for what you use.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Main Pricing Card */}
          <Card className="border-2 border-yellow-300 shadow-xl bg-gradient-to-br from-white to-yellow-50">
            <CardHeader className="pb-4">
              <div className="w-14 h-14 bg-yellow-400 rounded-xl flex items-center justify-center mb-4">
                <DollarSign className="w-8 h-8 text-black" />
              </div>
              <CardTitle className="text-3xl text-slate-900">Service Charges</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Base Service Charge */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-yellow-300">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <DollarSign className="w-5 h-5 text-yellow-700 mr-3" />
                    <span className="font-semibold text-slate-900">Base Service Charge</span>
                  </div>
                  <span className="text-2xl font-bold text-yellow-600">$48</span>
                </div>
                <p className="text-slate-600 text-sm ml-8">Flat rate</p>
              </div>

              {/* Additional Drop */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-yellow-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <DollarSign className="w-5 h-5 text-amber-700 mr-3" />
                    <span className="font-semibold text-slate-900">Additional Drop</span>
                  </div>
                  <span className="text-2xl font-bold text-amber-600">$10</span>
                </div>
                <p className="text-slate-600 text-sm ml-8">Per extra stop</p>
              </div>

              {/* Waiting Charges */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-yellow-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-yellow-700 mr-3" />
                    <span className="font-semibold text-slate-900">Waiting Charges</span>
                  </div>
                  <span className="text-2xl font-bold text-yellow-600">$24</span>
                </div>
                <p className="text-slate-600 text-sm ml-8">Per 30 minutes</p>
              </div>

              {/* After Hours */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-amber-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-amber-700 mr-3" />
                    <span className="font-semibold text-slate-900">After Hours Service</span>
                  </div>
                  <span className="text-2xl font-bold text-amber-600">$60</span>
                </div>
                <p className="text-slate-600 text-sm ml-8">After 4:00 AM</p>
              </div>

              {/* Cancellation Fee */}
              <div className="bg-white rounded-xl p-6 shadow-md border-2 border-red-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <DollarSign className="w-5 h-5 text-red-700 mr-3" />
                    <span className="font-semibold text-slate-900">Cancellation Fee</span>
                  </div>
                  <span className="text-2xl font-bold text-red-600">$30</span>
                </div>
                <p className="text-slate-600 text-sm ml-8">If cancelled after booking</p>
              </div>

              {/* Operating Hours */}
              <div className="bg-yellow-100 rounded-xl p-6 border-2 border-yellow-300">
                <h4 className="font-semibold text-slate-900 mb-3">Operating Hours</h4>
                <p className="text-slate-700">
                  <span className="font-semibold">Regular Hours:</span> 9:00 PM - 4:00 AM
                </p>
                <p className="text-sm text-slate-600 mt-2">
                  Available 7 days a week
                </p>
                <p className="text-sm text-amber-700 mt-2 font-semibold">
                  After hours services available
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Features Card */}
          <Card className="border-2 border-yellow-200 shadow-xl">
            <CardHeader className="pb-4">
              <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-yellow-700" />
              </div>
              <CardTitle className="text-3xl text-slate-900">What's Included</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4 mb-8">
                {pricingFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0" />
                    <span className="text-slate-700 text-lg">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 mb-6 border border-yellow-200">
                <h4 className="font-semibold text-slate-900 mb-2 text-lg">Need a Custom Quote?</h4>
                <p className="text-slate-600 mb-4">
                  For corporate events, group bookings, or special occasions, contact us for personalized pricing.
                </p>
              </div>

              <Button
                onClick={handleWhatsApp}
                size="lg"
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg py-6 h-auto shadow-lg"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Get a Quote on WhatsApp
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Additional Info */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-yellow-200">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Transparent & Fair Pricing
            </h3>
            <p className="text-slate-600 leading-relaxed">
              We believe in honest pricing with no hidden fees. All charges are clearly communicated 
              before your ride. Contact us for specific route pricing or any questions about our rates.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
